import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Alert, AlertDescription } from 'components/ui/alert';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import {
  getWebAnalyticsOverview,
  getPageMetrics,
  getTrafficSources,
  getWebChurnAnalysis,
  getChurnTrend,
  getGameweekTimeline,
  getDataFreshness,
  getFeatureAdoption,
  getPowerUserContrast,
  getNewSubscriberDiscovery,
  getGameweekRhythm,
  getConversionPaths,
  getAcquisitionFunnel,
  getDormantSubscribers,
  getCancelFlowFunnel,
  getChurnContext,
} from '../../../services/api';
import LoadingState from '../../shared/LoadingState';
import OverviewTab from './OverviewTab';
import ProductUsageTab from './ProductUsageTab';
import AcquisitionTab from './AcquisitionTab';
import RetentionTab from './RetentionTab';
import ChurnTab from './ChurnTab';

const TAB_LABELS = ['Overview', 'Product Usage', 'Acquisition', 'Retention & Churn', 'User Journey'];

export default function WebAnalytics() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [gameweekFilter, setGameweekFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [filtersDirty, setFiltersDirty] = useState(false);

  // Data state
  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [contrast, setContrast] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [rhythm, setRhythm] = useState(null);
  const [conversionPaths, setConversionPaths] = useState(null);
  const [acquisitionFunnel, setAcquisitionFunnel] = useState(null);
  const [dormant, setDormant] = useState(null);
  const [cancelFunnel, setCancelFunnel] = useState(null);
  const [churnContext, setChurnContext] = useState(null);
  const [churn, setChurn] = useState(null);
  const [churnLoading, setChurnLoading] = useState(false);
  const [churnTrend, setChurnTrend] = useState(null);
  const [journeyWpId, setJourneyWpId] = useState(null);
  const [gameweeks, setGameweeks] = useState(null);
  const [freshness, setFreshness] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const getFilters = useCallback(() => {
    const filters = {};
    if (startDate) filters.start_date = startDate;
    if (endDate) filters.end_date = endDate;
    if (propertyFilter) filters.property = propertyFilter;
    if (phaseFilter) filters.phase = phaseFilter;
    return filters;
  }, [startDate, endDate, propertyFilter, phaseFilter]);

  // Fetch overview data
  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWebAnalyticsOverview(getFilters());
      setOverview(res.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching web analytics overview:', err);
      setError(err.message || 'Failed to load web analytics');
      setLoading(false);
    }
  }, [getFilters]);

  // allSettled + fallbacks everywhere: one failed endpoint must not leave a
  // whole tab stuck on its loading spinner (the Aug 2026 breakage mode).
  const settle = (res, fallback) => (res.status === 'fulfilled' ? res.value : fallback);
  const reportFailures = (label, results) => {
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      failed.forEach((r) => console.error(`Error fetching ${label}:`, r.reason));
      toast.error(`Failed to load ${failed.length} of ${results.length} ${label} datasets`);
    }
  };

  // Fetch product usage tab data
  const fetchProductUsage = useCallback(async () => {
    const filters = getFilters();
    const results = await Promise.allSettled([
      getFeatureAdoption(filters),
      getPowerUserContrast(filters),
      getNewSubscriberDiscovery(),
      getGameweekRhythm(),
      getPageMetrics(filters),
    ]);
    const [adoptionRes, contrastRes, discoveryRes, rhythmRes, pagesRes] = results;
    setAdoption(settle(adoptionRes, { data: [] }));
    setContrast(settle(contrastRes, { data: [] }));
    setDiscovery(settle(discoveryRes, { data: [] }));
    setRhythm(settle(rhythmRes, { data: [] }));
    setPages(settle(pagesRes, { data: [] }).data || []);
    reportFailures('product usage', results);
  }, [getFilters]);

  // Fetch acquisition tab data
  const fetchAcquisition = useCallback(async () => {
    const filters = getFilters();
    const results = await Promise.allSettled([
      getConversionPaths(filters),
      getAcquisitionFunnel(),
      getTrafficSources(filters),
    ]);
    const [pathsRes, funnelRes, trafficRes] = results;
    setConversionPaths(settle(pathsRes, { segments: [], top_paths: [], histogram: [] }));
    setAcquisitionFunnel(settle(funnelRes, { data: [] }));
    setTraffic(settle(trafficRes, { data: [] }).data || []);
    reportFailures('acquisition', results);
  }, [getFilters]);

  // Fetch retention tab data
  const fetchRetention = useCallback(async () => {
    const filters = getFilters();
    const results = await Promise.allSettled([
      getDormantSubscribers(),
      getCancelFlowFunnel(filters),
      getChurnContext(filters),
    ]);
    const [dormantRes, cancelRes, contextRes] = results;
    setDormant(settle(dormantRes, { data: [], tiers: {} }));
    setCancelFunnel(settle(cancelRes, { funnel: [], has_data: false }));
    setChurnContext(settle(contextRes, { classes: {}, monthly: [], weekly: [], reasons: [] }));
    reportFailures('retention', results);
  }, [getFilters]);

  // Fetch churn
  const fetchChurn = useCallback(async () => {
    setChurnLoading(true);
    const filters = getFilters();
    const [res, trendRes] = await Promise.allSettled([
      getWebChurnAnalysis(filters),
      getChurnTrend(),
    ]);
    if (res.status === 'fulfilled') {
      setChurn(res.value);
    } else {
      console.error('Error fetching churn:', res.reason);
      toast.error('Failed to load churn analysis');
    }
    if (trendRes.status === 'fulfilled') {
      setChurnTrend(trendRes.value.data || []);
    } else {
      console.error('Error fetching churn trend:', trendRes.reason);
    }
    setChurnLoading(false);
  }, [getFilters]);

  // Fetch gameweek timeline + data freshness on mount
  useEffect(() => {
    getGameweekTimeline()
      .then((res) => setGameweeks(res.gameweeks || []))
      .catch((err) => console.error('Error fetching gameweek timeline:', err));
    getDataFreshness()
      .then((res) => setFreshness(res))
      .catch((err) => console.error('Error fetching data freshness:', err));
  }, []);

  // Initial load. NOTE: fetchOverview's identity changes with the filter state
  // (via getFilters), so this also re-fetches the overview whenever a filter
  // changes — intentional carry-over of the original call timing.
  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Lazy load tab data
  useEffect(() => {
    if (activeTab === 1 && !adoption) {
      fetchProductUsage();
    } else if (activeTab === 2 && !conversionPaths) {
      fetchAcquisition();
    } else if (activeTab === 3 && !dormant) {
      fetchRetention();
    } else if (activeTab === 4 && !churn) {
      fetchChurn();
    }
  }, [activeTab, adoption, conversionPaths, dormant, churn,
      fetchProductUsage, fetchAcquisition, fetchRetention, fetchChurn]);

  // Keyboard shortcuts: 1-4 switch tabs (single-admin desktop convenience).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      if (
        target instanceof Element &&
        target.closest('input, textarea, select, [contenteditable="true"], [role="listbox"], [role="option"]')
      ) {
        return;
      }
      const idx = ['1', '2', '3', '4', '5'].indexOf(e.key);
      if (idx !== -1) setActiveTab(idx);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleRefresh = () => {
    // Reset all cached data
    setOverview(null);
    setPages(null);
    setTraffic(null);
    setAdoption(null);
    setContrast(null);
    setDiscovery(null);
    setRhythm(null);
    setConversionPaths(null);
    setAcquisitionFunnel(null);
    setDormant(null);
    setCancelFunnel(null);
    setChurnContext(null);
    setChurn(null);
    setChurnTrend(null);
    setFiltersDirty(false);
    fetchOverview();
    if (activeTab === 1) fetchProductUsage();
    if (activeTab === 2) fetchAcquisition();
    if (activeTab === 3) fetchRetention();
    if (activeTab === 4) fetchChurn();
  };

  const handleGameweekChange = (rawVal) => {
    const val = rawVal === 'custom' ? '' : rawVal;
    setGameweekFilter(val);
    setFiltersDirty(true);
    // Phase options
    if (val === 'during_gameweeks' || val === 'between_gameweeks') {
      setPhaseFilter(val);
      setStartDate('');
      setEndDate('');
      return;
    }
    // Custom range or individual GW — clear phase
    setPhaseFilter('');
    if (val && gameweeks) {
      // eslint-disable-next-line eqeqeq
      const gw = gameweeks.find((g) => g.id == val);
      if (gw) {
        // Set date range: 1 day before trades_time → 1 day after last_kickoff (or deadline)
        const tradesDate = gw.trades_time ? new Date(gw.trades_time) : null;
        const endRef = gw.last_kickoff || gw.deadline_time;
        const endDateRef = endRef ? new Date(endRef) : null;
        if (tradesDate) {
          const start = new Date(tradesDate);
          start.setDate(start.getDate() - 1);
          setStartDate(start.toISOString().split('T')[0]);
        }
        if (endDateRef) {
          const end = new Date(endDateRef);
          end.setDate(end.getDate() + 1);
          setEndDate(end.toISOString().split('T')[0]);
        }
      }
    }
  };

  if (loading && !overview) {
    return <LoadingState message="Loading Web Analytics..." />;
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Error: {error}</AlertDescription>
        </Alert>
        <Button variant="outline" size="sm" onClick={handleRefresh}>Retry</Button>
      </div>
    );
  }

  const freshnessIndicator = (() => {
    if (!freshness?.latest_date) return null;
    const latest = new Date(freshness.latest_date + 'T00:00:00');
    const hoursAgo = (Date.now() - latest.getTime()) / (1000 * 60 * 60);
    const dotClass = hoursAgo < 26 ? 'bg-success' : hoursAgo < 48 ? 'bg-warning' : 'bg-error';
    const formatted = latest.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' });
    return (
      <div className="flex items-center gap-1.5">
        <span className={cn('h-2 w-2 rounded-full', dotClass)} />
        <span className="text-token-base text-muted-foreground">Data as of: {formatted}</span>
      </div>
    );
  })();

  return (
    <div className="w-full min-w-0">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-token-xl font-semibold">Web Analytics</h2>
          {freshnessIndicator}
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex w-[220px] flex-col gap-1.5">
            <Label className="text-token-base font-medium text-muted-foreground">Gameweek</Label>
            <Select
              value={gameweekFilter === '' ? 'custom' : String(gameweekFilter)}
              onValueChange={handleGameweekChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Custom Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom Range</SelectItem>
                <SelectItem value="during_gameweeks">During Gameweeks</SelectItem>
                <SelectItem value="between_gameweeks">Between Gameweeks</SelectItem>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Individual Gameweeks</SelectLabel>
                  {(gameweeks || []).map((gw) => (
                    <SelectItem key={gw.id} value={String(gw.id)}>
                      GW {gw.id}{gw.finished ? '' : ' (live)'}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex w-[170px] flex-col gap-1.5">
            <Label className="text-token-base font-medium text-muted-foreground" htmlFor="wa-start-date">Start Date</Label>
            <Input
              id="wa-start-date"
              type="date"
              className="h-10"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setGameweekFilter('');
                setPhaseFilter('');
                setFiltersDirty(true);
              }}
            />
          </div>

          <div className="flex w-[170px] flex-col gap-1.5">
            <Label className="text-token-base font-medium text-muted-foreground" htmlFor="wa-end-date">End Date</Label>
            <Input
              id="wa-end-date"
              type="date"
              className="h-10"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setGameweekFilter('');
                setPhaseFilter('');
                setFiltersDirty(true);
              }}
            />
          </div>

          <div className="flex w-[180px] flex-col gap-1.5">
            <Label className="text-token-base font-medium text-muted-foreground">Property</Label>
            <Select
              value={propertyFilter === '' ? 'all' : propertyFilter}
              onValueChange={(v) => {
                setPropertyFilter(v === 'all' ? '' : v);
                setFiltersDirty(true);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                <SelectItem value="web_app">Web App</SelectItem>
                <SelectItem value="storefront">Storefront</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            className="h-10"
            onClick={handleRefresh}
            disabled={loading}
            title={filtersDirty ? 'Filters changed since last fetch' : undefined}
          >
            Apply Filters
            {filtersDirty && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-warning" aria-hidden="true" />}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={String(activeTab)}
        onValueChange={(v) => setActiveTab(Number(v))}
        className="mb-4"
      >
        <TabsList>
          {TAB_LABELS.map((label, i) => (
            <TabsTrigger key={label} value={String(i)}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Tab Content — rendered conditionally (not via TabsContent) to preserve
          the lazy fetch-on-first-activation pattern. */}
      {activeTab === 0 && <OverviewTab data={overview} loading={loading} gameweeks={gameweeks} />}
      {activeTab === 1 && (
        <ProductUsageTab
          adoption={adoption}
          contrast={contrast}
          discovery={discovery}
          rhythm={rhythm}
          pages={pages}
        />
      )}
      {activeTab === 2 && (
        <AcquisitionTab
          conversionPaths={conversionPaths}
          acquisitionFunnel={acquisitionFunnel}
          traffic={traffic}
        />
      )}
      {activeTab === 3 && (
        <RetentionTab
          dormant={dormant}
          cancelFunnel={cancelFunnel}
          churnContext={churnContext}
          onOpenJourney={(wpId) => {
            setJourneyWpId(wpId);
            setActiveTab(4);
          }}
        />
      )}
      {activeTab === 4 && (
        <ChurnTab
          churn={churn}
          churnLoading={churnLoading}
          churnTrend={churnTrend}
          initialWpId={journeyWpId}
        />
      )}
    </div>
  );
}
